import type { ApolloError } from '@apollo/client';
import * as _ from 'lodash-es';
import type { Parchment } from 'quill/core';
import Logger from './Logger';
import attachmentSizeExceededToast from '../editor/attachmentSizeExceededToast';
import isFileOverlarge from './network/isFileOverlarge';
import AssetUploader from '../editor/AssetUploader';
import type { AttachmentFile } from '../editor/formats/Attachment';
import type Attachment from '../editor/formats/Attachment';
import type AttachmentMention from '../editor/formats/AttachmentMention';
import type FileBlot from '../editor/formats/FileBlot';
import Queue from '../editor/utils/Queue';
import showEditorToast from '../editor/utils/showEditorToast';
import { EntityUploadType } from '../graphql/generated/graphql';

const UPLOADING_PREFIX = 'UPLOADING:';

export const isAttachmentUploaded = ({ source }: AttachmentFile) =>
  !source.startsWith(UPLOADING_PREFIX);

export const filterValidFiles = (files: File[]) => {
  const [overlargeFiles, validFiles] = _.partition(files, isFileOverlarge);
  if (overlargeFiles.length) {
    overlargeFiles.forEach(() => {
      showEditorToast(attachmentSizeExceededToast);
    });
  }
  return validFiles;
};

export class CopyFileFromUrl {
  constructor(
    public source: string,
    public name: string,
    public size: number,
  ) {}

  toJSON() {
    return JSON.stringify({ source: '', name: this.name, size: this.size });
  }
}

class AttachmentUploader {
  // Remote attachment updates will recreate blots so we need to keep track all attachment blots
  // so that once an uploading is finished, we can find the related blot
  // even if it and the underlying DOM node has been recreated.
  private activeAttachmentBlots = new Set<
    Attachment | AttachmentMention | FileBlot
  >();
  private uploadQueue: Queue<string | null> = new Queue();
  private uploadClientId = `${UPLOADING_PREFIX}${Math.random()
    .toString(36)
    .substring(2, 10)}`;
  private fileIdMap = new WeakMap<File | CopyFileFromUrl, string>();

  uploadFiles(files: (File | CopyFileFromUrl)[], blot: Parchment.Blot) {
    if (!files.length) return;

    const assetUploader = AssetUploader.getForBlot(blot);

    if (!assetUploader) {
      showEditorToast({
        level: 'error',
        message: 'Attachment could not be uploaded',
      });
      Logger.error('Unable to find asset uploader for the current editor');
      return;
    }

    files.forEach((file) => {
      this.uploadQueue
        .enqueue(() =>
          assetUploader.sendFileToServer(
            file instanceof File ? file : file.source,
            EntityUploadType.Attachment,
          ),
        )
        .catch((error) => this.handleUploadError(error))
        .then((source) => this.cleanAfterUpload(file, source));
    });
  }

  handleUploadError(error: ApolloError) {
    const isPayloadTooLarge =
      error.networkError &&
      'statusCode' in error.networkError &&
      error.networkError.statusCode === 413;

    if (isPayloadTooLarge) {
      showEditorToast(attachmentSizeExceededToast);
    } else {
      showEditorToast({
        level: 'error',
        message: 'Attachment could not be uploaded',
      });
    }
    return null;
  }

  cleanAfterUpload(file: File | CopyFileFromUrl, source: string | null) {
    this.activeAttachmentBlots.forEach((blot) => {
      blot.cleanUpload(this.getIdForFile(file), source);
    });
  }

  addActiveAttachmentBlot(blot: Attachment | AttachmentMention | FileBlot) {
    this.activeAttachmentBlots.add(blot);
  }

  removeActiveAttachmentBlot(blot: Attachment | AttachmentMention | FileBlot) {
    this.activeAttachmentBlots.delete(blot);
  }

  shouldShowUploadingAttachment({ source }: AttachmentFile) {
    return source.startsWith(this.uploadClientId);
  }

  getIdForFile(file: File | CopyFileFromUrl) {
    let id = this.fileIdMap.get(file);
    if (!id) {
      id = _.uniqueId(this.uploadClientId);
      this.fileIdMap.set(file, id);
    }
    return id;
  }

  normalizeToAttachmentFiles(
    value: (AttachmentFile | File | CopyFileFromUrl)[],
  ): AttachmentFile[] {
    return value.map((item) =>
      item instanceof File || item instanceof CopyFileFromUrl
        ? { source: this.getIdForFile(item), size: item.size, name: item.name }
        : item,
    );
  }
}

export default new AttachmentUploader();
